import { ReactNode, useEffect, useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { navPath } from '@/constant';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, ArrowLeft, LogIn } from 'lucide-react';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: string[];
  fallbackPath?: string;
}

const AuthContext = createContext<{ isAuthorized: boolean }>({ isAuthorized: false });

export const useAuth = () => useContext(AuthContext);

const RoleGuard = ({ children, allowedRoles }: RoleGuardProps) => {
  const [authState, setAuthState] = useState<{
    authorized: boolean;
    checked: boolean;
  }>({
    authorized: false,
    checked: false,
  });

  useEffect(() => {
    const role = localStorage.getItem('role');

    const timer = setTimeout(() => {
      const isAuthorized = !!role && allowedRoles.includes(role);
      setAuthState({
        authorized: isAuthorized,
        checked: true,
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [allowedRoles]);

  if (!authState.checked) {
    return <AuthCheckingPlaceholder />;
  }

  if (authState.authorized) {
    return <AuthContext.Provider value={{ isAuthorized: true }}>{children}</AuthContext.Provider>;
  }

  return <UnauthorizedView />;
};

const AuthCheckingPlaceholder = () => (
  <div className='flex justify-center items-center h-full'>
    <div className='animate-pulse flex flex-col items-center gap-4'>
      <div className='rounded-lg bg-muted h-8 w-48'></div>
      <div className='h-4 bg-muted rounded w-64'></div>
    </div>
  </div>
);

const UnauthorizedView = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      localStorage.removeItem('role');
      localStorage.removeItem('username');

      navigate(navPath.login);
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out. Please try again.');
    }
  };

  return (
    <div className='flex justify-center items-center min-h-full p-4'>
      <Card className='max-w-md w-full shadow-xl border-0 bg-gradient-to-br from-background to-muted/20'>
        <CardHeader className='text-center pb-8 pt-12'>
          <div className='mx-auto bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6 shadow-sm'>
            <Lock className='h-8 w-8 text-red-500' />
          </div>
          <div className='space-y-3'>
            <h1 className='text-2xl font-bold text-foreground tracking-tight'>Access Denied</h1>
            <p className='text-muted-foreground text-base leading-relaxed'>
              You don't have the necessary permissions to view this page. Please contact your administrator if you believe this is
              an error.
            </p>
          </div>
        </CardHeader>

        <CardContent className='px-8 pb-2'>
          <div className='bg-muted/30 rounded-xl p-4 border border-border/50'>
            <p className='text-sm text-muted-foreground text-center'>
              If you were recently granted access, try logging out and back in to refresh your permissions.
            </p>
          </div>
        </CardContent>

        <CardFooter className='flex gap-3 p-8 pt-6'>
          <Button variant='outline' className='flex-1 h-11 font-medium' onClick={() => window.history.back()}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Go Back
          </Button>
          <Button
            className='flex-1 h-11 font-medium bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80'
            onClick={handleLogout}
          >
            <LogIn className='h-4 w-4 mr-2' />
            Sign In
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RoleGuard;
